import * as React from 'react';
import styles from './index.less';
import * as AHooks from 'ahooks';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import FComponentsLib from '@freelog/components-lib';
import { Dropdown } from 'antd';
import FIcons from '@/components/FIcons';
import FSimpleBar from '../FSimpleBar';
import CascaderMenu from './CascaderMenu';
import type { Option, SelectedValue, ServerData, SubjectTypeItem } from './types';
import { buildOptionTree, toSubjectTypeParam } from './types';

/** 「全部」选项的默认值，用于列表筛选初始态 */
export const resourceTypeCodes_all: SelectedValue = {
  value: '#all',
  label: FI18n.i18nNext.tAuto('filter_resourcetype_option_all'),
  values: ['#all'],
  labels: [FI18n.i18nNext.tAuto('filter_resourcetype_option_all')],
};

/** 兼容旧引用，与 resourceTypeCodes_all 相同 */
export const resourceTypeCodes_all2 = resourceTypeCodes_all;

interface FResourceTypeFilter3Props {
  value: SelectedValue;
  omitTheme?: boolean;
  onChange?(value: SelectedValue): void;
  subjectTypeArr: SubjectTypeItem[];
}

function FResourceTypeFilter3({
  value,
  omitTheme = false,
  onChange,
  subjectTypeArr,
}: FResourceTypeFilter3Props) {
  const [_localRecently, set_localRecently] = AHooks.useLocalStorageState<
    { value: string; labels: string[] }[]
  >('FResourceTypeFilter-$localRecently', { defaultValue: [] });
  const [$recommend, set$recommend] = React.useState<
    { value: string; labels: string[] }[]
  >([]);
  const [$options, set$options] = React.useState<Option[]>([]);
  const [_isOpen, set_isOpen] = React.useState(false);

  const subjectTypeParam = React.useMemo(
    () => toSubjectTypeParam(subjectTypeArr),
    [subjectTypeArr],
  );

  AHooks.useMount(async () => {
    const { data } = await FServiceAPI.Resource.resourceTypes({
      category: 1,
      isMine: false,
      status: 1,
      ...(subjectTypeParam && {
        subjectType: subjectTypeParam as unknown as 1 | 4 | 5,
      }),
    });
    let list: ServerData[] = data || [];
    if (omitTheme) {
      list = list.filter((d) => d.name !== '主题');
    }
    set$options(buildOptionTree(list, null, subjectTypeArr));
  });

  AHooks.useMount(async () => {
    const { data } = await FServiceAPI.Resource.listSimple4Recently({
      ...(subjectTypeParam && {
        subjectType: subjectTypeParam as unknown as 1 | 4 | 5,
      }),
    });
    if (!data) return;
    set$recommend(
      data
        .filter((r) =>
          _localRecently.every((lr) => lr.value.replace('#all', '') !== r.code),
        )
        .slice(0, 6)
        .map((r) => ({ value: r.code, labels: r.names.split('/') })),
    );
  });

  function onDropdownChange(op: Option) {
    if (op.value !== '#all') {
      set_localRecently([
        { value: op.value, labels: op.labels },
        ..._localRecently.filter((i) => i.value !== op.value).slice(0, 5),
      ]);
    }
    onChange?.(op);
    set_isOpen(false);
  }

  const displayText =
    value.labels
      .filter((l) => l !== FI18n.i18nNext.tAuto('filter_resourcetype_option_all'))
      .join(' / ') ||
    FI18n.i18nNext.tAuto('filter_resourcetype_option_all');

  const recentAndRecommend = [..._localRecently, ...$recommend].slice(0, 6);
  const allLabel = FI18n.i18nNext.tAuto('filter_resourcetype_option_all');
  const overlayRef = React.useRef<HTMLDivElement>(null);

  return (
    <Dropdown
      open={_isOpen}
      onOpenChange={set_isOpen}
      trigger={['hover']}
      overlayClassName={styles.overlayClassName}
      destroyOnHidden
      popupRender={() => (
        <div
          ref={overlayRef}
          style={{ overflow: 'visible', position: 'relative' }}
        >
          <FSimpleBar maxHeight={540} width="fit-content">
          <div className={styles.overlay}>
            {recentAndRecommend.length > 0 && (
              <>
                {subjectTypeArr.includes('resource') && (
                  <div className={styles.recommend}>
                    <FComponentsLib.FContentText
                      text={FI18n.i18nNext.tAuto('filter_resourcetype_hint')}
                      type="additional2"
                    />
                    <div style={{ height: 20 }} />
                    <div className={styles.recommendResourceTypes}>
                      {recentAndRecommend.map((r) => {
                        const showLabels = r.labels.filter((l) => l !== allLabel);
                        return (
                          <label
                            key={r.value}
                            onClick={() =>
                              onDropdownChange({
                                value: r.value,
                                values: [],
                                labels: r.labels,
                                label: '',
                                children: [],
                              })
                            }
                          >
                            {showLabels.join('/') || allLabel}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                {subjectTypeArr.includes('resource') && (
                  <>
                    <div style={{ padding: '0 20px' }}>
                      <FComponentsLib.FContentText
                        text={FI18n.i18nNext.tAuto('filter_resourcetype_hint2')}
                        type="additional2"
                      />
                    </div>
                    <div style={{ height: 10 }} />
                  </>
                )}
                {subjectTypeArr.includes('collection') && (
                  <div style={{ height: 10 }} />
                )}
              </>
            )}

            <div className={styles.FCascader}>
              <CascaderMenu
                options={$options}
                zIndex={10000}
                onSelect={onDropdownChange}
                getPopupContainer={() =>
                  overlayRef.current || document.body
                }
              />
            </div>
            <div style={{ height: 10 }} />
          </div>
        </FSimpleBar>
        </div>
      )}
      autoAdjustOverflow={false}
    >
      <div className={styles.square}>
        <FComponentsLib.FContentText
          text={displayText}
          type="normal"
          style={{ fontSize: 16 }}
        />
        {_isOpen ? (
          <FIcons.FUp style={{ fontSize: 12 }} />
        ) : (
          <FIcons.FDown style={{ fontSize: 12 }} />
        )}
      </div>
    </Dropdown>
  );
}

export default FResourceTypeFilter3;
